import { useState, useCallback, useRef, useEffect } from 'react';

interface UseLoadingOptions {
  initialLoading?: boolean;
  timeout?: number;
  onTimeout?: () => void;
  onError?: (error: Error) => void;
}

interface UseLoadingReturn {
  isLoading: boolean;
  error: Error | null;
  startLoading: () => void;
  stopLoading: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  withLoading: <T>(promise: Promise<T>) => Promise<T>;
  reset: () => void;
}

/**
 * Hook to manage loading states with optional timeout and error handling
 * 
 * @example
 * ```tsx
 * const { isLoading, error, withLoading, startLoading, stopLoading } = useLoading({
 *   timeout: 10000,
 *   onTimeout: () => toast.error('Request timed out'),
 * });
 * 
 * // Wrap an async operation
 * const data = await withLoading(fetchData());
 * 
 * // Or use manual control
 * startLoading();
 * try {
 *   const result = await fetchData();
 * } finally {
 *   stopLoading();
 * }
 * ```
 */
export function useLoading(options: UseLoadingOptions = {}): UseLoadingReturn {
  const {
    initialLoading = false,
    timeout,
    onTimeout,
    onError,
  } = options;

  const [isLoading, setIsLoading] = useState(initialLoading);
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const startLoading = useCallback(() => {
    setIsLoading(true);
    setError(null);

    // Set up timeout if specified
    if (timeout && timeout > 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsLoading(false);
        setError(new Error('Timeout: Operation took too long'));
        onTimeout?.();
      }, timeout);
    }
  }, [timeout, onTimeout]);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    if (loading) {
      setIsLoading(true);
      setError(null);
    } else {
      setIsLoading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, []);

  const setErrorState = useCallback((err: Error | null) => {
    setError(err);
    if (err) {
      setIsLoading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      onError?.(err);
    }
  }, [onError]);

  const withLoading = useCallback(async <T,>(promise: Promise<T>): Promise<T> => {
    startLoading();
    try {
      const result = await promise;
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setErrorState(error);
      throw error;
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading, setErrorState]);

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return {
    isLoading,
    error,
    startLoading,
    stopLoading,
    setLoading,
    setError: setErrorState,
    withLoading,
    reset,
  };
}

interface UseAsyncOptions<T> {
  initialData?: T;
  immediate?: boolean;
  timeout?: number;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

interface UseAsyncReturn<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
  execute: (...args: unknown[]) => Promise<T>;
  reset: () => void;
}

/**
 * Hook for handling async data fetching with loading states
 * 
 * @example
 * ```tsx
 * const { data, isLoading, error, execute } = useAsync(
 *   () => fetchEvents(),
 *   { immediate: true }
 * );
 * 
 * // Manual execution
 * const handleRefresh = () => execute();
 * ```
 */
export function useAsync<T>(
  asyncFunction: () => Promise<T>,
  options: UseAsyncOptions<T> = {}
): UseAsyncReturn<T> {
  const {
    initialData,
    immediate = false,
    timeout,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState<T | undefined>(initialData);
  const { isLoading, error, withLoading, reset: resetLoading } = useLoading({
    timeout,
    onError,
  });

  const execute = useCallback(async (..._args: unknown[]): Promise<T> => {
    try {
      const result = await withLoading(asyncFunction());
      setData(result);
      onSuccess?.(result);
      return result;
    } catch (err) {
      throw err;
    }
  }, [asyncFunction, withLoading, onSuccess]);

  const reset = useCallback(() => {
    setData(initialData);
    resetLoading();
  }, [initialData, resetLoading]);

  // Execute immediately if requested
  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [immediate, execute]);

  return {
    data,
    isLoading,
    error,
    execute,
    reset,
  };
}

interface UseSubmitOptions<T, R> {
  timeout?: number;
  resetOnSuccess?: boolean;
  onSuccess?: (result: R, data: T) => void;
  onError?: (error: Error, data: T) => void;
}

interface UseSubmitReturn<T, R> {
  isSubmitting: boolean;
  submitError: Error | null;
  submitResult: R | undefined;
  submit: (data: T) => Promise<R>;
  reset: () => void;
}

/**
 * Hook for handling form submissions with loading states
 * 
 * @example
 * ```tsx
 * const { isSubmitting, submitError, submit } = useSubmit(
 *   async (formData) => {
 *     const response = await api.submitContact(formData);
 *     return response;
 *   },
 *   {
 *     onSuccess: () => toast.success('Message sent!'),
 *     onError: (err) => toast.error(err.message),
 *   }
 * );
 * 
 * const handleSubmit = (e) => {
 *   e.preventDefault();
 *   submit(formData);
 * };
 * ```
 */
export function useSubmit<T, R = unknown>(
  submitFunction: (data: T) => Promise<R>,
  options: UseSubmitOptions<T, R> = {}
): UseSubmitReturn<T, R> {
  const {
    timeout = 30000,
    resetOnSuccess = false,
    onSuccess,
    onError,
  } = options;

  const [submitResult, setSubmitResult] = useState<R | undefined>(undefined);
  const { isLoading, error, withLoading, reset: resetLoading } = useLoading({
    timeout,
  });

  const submit = useCallback(async (data: T): Promise<R> => {
    try {
      const result = await withLoading(submitFunction(data));
      setSubmitResult(result);
      onSuccess?.(result, data);
      
      if (resetOnSuccess) {
        resetLoading();
        setSubmitResult(undefined);
      }
      
      return result;
    } catch (err) {
      const submitErr = err instanceof Error ? err : new Error(String(err));
      onError?.(submitErr, data);
      throw submitErr;
    }
  }, [submitFunction, withLoading, onSuccess, onError, resetOnSuccess, resetLoading]);

  const reset = useCallback(() => {
    resetLoading();
    setSubmitResult(undefined);
  }, [resetLoading]);

  return {
    isSubmitting: isLoading,
    submitError: error,
    submitResult,
    submit,
    reset,
  };
}

export default useLoading;
